(() => {
  if (customElements.get('ron-merchandising')) return;

  class RonMerchandising extends HTMLElement {
    connectedCallback() {
      this.destroyCycle();
      this.controller?.abort();
      this.controller = new AbortController();
      this.requests = new WeakMap();
      this.cancelTransitions();
      this.transitions = new Set();
      this.transitionPreference = matchMedia('(prefers-reduced-motion: reduce)');
      this.transitionPreference.addEventListener('change', () => {
        if (this.transitionPreference.matches) this.cancelTransitions();
      }, { signal: this.controller.signal });
      this.tabs = Array.from(this.querySelectorAll('[data-ron-merch-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-ron-merch-panel]'));
      if (!this.tabs.length || this.tabs.length !== this.panels.length) return;

      const signal = this.controller.signal;
      this.querySelector('[data-ron-merch-tabs]')?.setAttribute('role', 'tablist');
      this.tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', this.panels[index].id);
        this.panels[index].setAttribute('role', 'tabpanel');
        this.panels[index].tabIndex = 0;
      });
      this.querySelectorAll('[data-ron-merch-controls]').forEach(control => {
        control.hidden = false;
      });
      this.querySelectorAll('[data-ron-merch-gallery]').forEach(gallery => {
        gallery.removeAttribute('aria-busy');
      });
      const restoredIndex = this.panels.findIndex(panel => panel.id === this.selectedPanelId);
      this.selectTab(restoredIndex < 0 ? 0 : restoredIndex);
      this.setAttribute('data-ron-merch-ready', '');
      this.groundMobile = this.matches('body.ron-home #MainContent[data-template="index"] .ron-ground-section')
        ? matchMedia('(max-width: 760px)') : null;
      if (this.groundMobile) {
        this.syncGroundLayout();
        this.groundMobile.addEventListener('change', () => {
          this.syncGroundLayout();
          this.syncCycle();
        }, { signal });
      }
      this.addEventListener('click', event => {
        const tab = event.target.closest('[data-ron-merch-tab]');
        if (tab && this.tabs.includes(tab)) {
          event.preventDefault();
          this.pauseCycle();
          this.selectTab(this.tabs.indexOf(tab));
          return;
        }
        const imageButton = event.target.closest('[data-ron-merch-image]');
        if (imageButton && this.contains(imageButton)) { this.pauseCycle(); this.selectImage(imageButton); }
      }, { signal });
      this.addEventListener('keydown', event => {
        const tab = event.target.closest('[data-ron-merch-tab]');
        if (!tab || !this.tabs.includes(tab)) return;
        const current = this.tabs.indexOf(tab);
        let next = current;
        const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.tabs.length - 1;
        else if (event.key === 'ArrowRight') next = (current + direction + this.tabs.length) % this.tabs.length;
        else if (event.key === 'ArrowLeft') next = (current - direction + this.tabs.length) % this.tabs.length;
        else if (event.key === ' ') next = current;
        else return;
        event.preventDefault();
        this.pauseCycle();
        this.selectTab(next);
        this.tabs[next].focus();
      }, { signal });
      document.addEventListener('shopify:block:select', event => {
        const index = this.panels.findIndex(panel => panel.dataset.blockId === event.detail?.blockId);
        if (index >= 0) { this.pauseCycle(); this.selectTab(index); }
      }, { signal });
      this.initCycle(signal);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.destroyCycle();
      this.cancelTransitions();
    }

    cancelTransitions() {
      this.transitions?.forEach(animation => animation.cancel());
      this.transitions?.clear();
    }

    revealSelection(node, duration = 280) {
      if (this.transitionPreference?.matches || !node.animate) return;
      // Opacity only: all product geometry and existing image crops stay fixed.
      const animation = node.animate([{ opacity: .38 }, { opacity: 1 }], {
        duration, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'none'
      });
      this.transitions.add(animation);
      animation.finished.then(() => this.transitions.delete(animation), () => this.transitions.delete(animation));
    }

    syncGroundLayout() {
      const compact = this.groundMobile?.matches;
      for (const panel of this.panels) {
        panel.setAttribute('role', compact ? 'group' : 'tabpanel');
        if (compact) panel.removeAttribute('tabindex');
        else panel.tabIndex = 0;
      }
    }

    selectTab(index) {
      if (!this.panels[index]) return;
      const changed = this.selectedTab !== index;
      if (changed) this.cancelTransitions();
      this.selectedTab = index;
      this.selectedPanelId = this.panels[index].id;
      this.tabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        this.panels[tabIndex].hidden = !selected;
      });
      const panel = this.panels[index];
      const collectionLink = this.querySelector('[data-ron-merch-collection]');
      if (collectionLink) {
        const url = panel.dataset.collectionUrl;
        collectionLink.hidden = !url;
        if (url) collectionLink.setAttribute('href', url);
        else collectionLink.removeAttribute('href');
      }
      const count = this.querySelector('[data-ron-merch-count]');
      if (count) count.textContent = panel.dataset.countLabel || '';
      if (changed && this.hasAttribute('data-ron-merch-ready') && this.classList.contains('ron-ground-section')) {
        this.revealSelection(panel);
      }
    }

    initCycle(signal) {
      const controls = this.querySelector('[data-ron-cycle-controls]');
      if (!this.hasAttribute('data-ron-cycle') || !this.closest('#MainContent[data-template="index"]') || !controls || this.tabs.length < 2) return;
      const preference = matchMedia('(prefers-reduced-motion: reduce)');
      const supportsVisibility = 'IntersectionObserver' in window;
      this.cycleToggle = controls.querySelector('[data-ron-cycle-toggle]');
      this.cycleToggle.hidden = !supportsVisibility;
      this.cycle = {
        requested: (this.cycleIntent ?? this.dataset.ronCycleEnabled !== 'false') && supportsVisibility && !preference.matches && !window.Shopify?.designMode,
        visible: false, hovered: false, allowToggleFocus: false, pointerRequested: null,
        delay: Math.max(5, Math.min(12, Number(this.dataset.ronCycleInterval) || 7)) * 1000,
        timer: null,
      };
      controls.hidden = false;
      for (const button of controls.querySelectorAll('[data-ron-cycle-step]')) button.addEventListener('click', () => {
        this.pauseCycle();
        this.selectTab((this.selectedTab + Number(button.dataset.ronCycleStep) + this.tabs.length) % this.tabs.length);
      }, { signal });
      this.cycleToggle.addEventListener('pointerdown', event => {
        if (this.cycle && event.button === 0) this.cycle.pointerRequested = this.cycle.requested;
      }, { signal });
      this.cycleToggle.addEventListener('click', event => {
        if (!this.cycle) return;
        // Pointer focus arrives before click. Preserve a Pause click's intent even
        // though focus correctly stops rotation first. Keyboard Play is explicit.
        const prior = event.detail && this.cycle.pointerRequested !== null ? this.cycle.pointerRequested : this.cycle.requested;
        this.cycle.pointerRequested = null;
        this.cycle.requested = !prior;
        this.cycleIntent = this.cycle.requested;
        this.cycle.allowToggleFocus = this.cycle.requested;
        this.syncCycle();
      }, { signal });
      this.addEventListener('pointerenter', event => {
        if (!this.cycle || event.pointerType === 'touch' || !matchMedia('(hover: hover)').matches) return;
        this.cycle.hovered = true; this.syncCycle();
      }, { signal });
      this.addEventListener('pointerleave', () => {
        if (!this.cycle) return;
        this.cycle.hovered = false; this.syncCycle();
      }, { signal });
      this.addEventListener('focusin', () => this.pauseCycle(), { signal });
      this.addEventListener('focusout', () => queueMicrotask(() => {
        if (this.cycle && this.isConnected) this.syncCycle();
      }), { signal });
      preference.addEventListener('change', () => {
        if (preference.matches) this.pauseCycle();
        else this.syncCycle();
      }, { signal });
      document.addEventListener('visibilitychange', () => this.syncCycle(), { signal });
      document.addEventListener('shopify:section:unload', event => {
        if (event.target instanceof Element && event.target.contains(this)) this.destroyCycle();
      }, { signal });
      window.addEventListener('pagehide', () => {
        this.pauseCycle(); this.cycleObserver?.disconnect();
        if (this.cycle) this.cycle.visible = false;
      }, { signal });
      window.addEventListener('pageshow', event => {
        if (event.persisted && this.cycle) this.cycleObserver?.observe(this);
      }, { signal });
      if (supportsVisibility) {
        this.cycleObserver = new IntersectionObserver(entries => {
          if (!this.cycle) return;
          this.cycle.visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= .2);
          this.syncCycle();
        }, { threshold: [0, .2] });
        this.cycleObserver.observe(this);
      }
      this.syncCycle();
    }

    pauseCycle() {
      if (!this.cycle) return;
      this.cycle.requested = false;
      this.cycleIntent = false;
      this.cycle.allowToggleFocus = false;
      this.syncCycle();
    }

    canCycle() {
      const state = this.cycle;
      const focusInside = this.contains(document.activeElement);
      return state && this.isConnected && state.requested && state.visible && !state.hovered && !document.hidden &&
        !this.groundMobile?.matches &&
        (!focusInside || (state.allowToggleFocus && document.activeElement === this.cycleToggle));
    }

    syncCycle() {
      if (!this.cycle) return;
      clearTimeout(this.cycle.timer);
      this.cycle.timer = null;
      const label = this.cycleToggle.querySelector('[data-ron-cycle-label]');
      label.textContent = this.cycle.requested ? this.cycleToggle.dataset.pause : this.cycleToggle.dataset.play;
      this.cycleToggle.setAttribute('aria-label', this.cycle.requested ? this.cycleToggle.dataset.pauseLabel : this.cycleToggle.dataset.playLabel);
      this.dataset.ronCycleState = this.canCycle() ? 'playing' : this.cycle.requested ? 'waiting' : 'paused';
      if (!this.canCycle()) return;
      this.cycle.timer = setTimeout(() => {
        if (!this.canCycle()) return;
        this.cycle.timer = null;
        this.selectTab((this.selectedTab + 1) % this.tabs.length);
        this.syncCycle();
      }, this.cycle.delay);
    }

    destroyCycle() {
      if (this.cycle) clearTimeout(this.cycle.timer);
      this.cycleObserver?.disconnect();
      this.cycleObserver = null;
      this.cycle = null;
      this.removeAttribute('data-ron-cycle-state');
    }

    async selectImage(button) {
      const gallery = button.closest('[data-ron-merch-gallery]');
      const photo = gallery?.querySelector('.ron-merch-main-image');
      const source = button.dataset.ronMerchImage;
      if (!gallery || !photo || !source) return;
      const request = {};
      this.requests.set(gallery, request);
      gallery.setAttribute('aria-busy', 'true');
      const candidate = new Image();
      candidate.src = source;
      try {
        await candidate.decode();
        if (!this.isConnected || this.requests.get(gallery) !== request) return;
        this.cancelTransitions();
        photo.removeAttribute('srcset');
        photo.src = source;
        photo.alt = button.dataset.imageAlt || '';
        photo.classList.toggle('ron-rotation-detail-view', button.dataset.imageDetail === 'true');
        this.revealSelection(photo, 240);
        gallery.querySelectorAll('[data-ron-merch-image]').forEach(view => {
          view.setAttribute('aria-pressed', String(view === button));
        });
        const label = gallery.querySelector('[data-ron-merch-image-label]');
        if (label) label.textContent = button.dataset.imageLabel || '';
        const status = this.querySelector('[data-ron-merch-status]');
        if (status) status.textContent = '';
      } catch {
        if (this.requests.get(gallery) === request) {
          const status = this.querySelector('[data-ron-merch-status]');
          if (status) status.textContent = status.dataset.errorText || '';
        }
      } finally {
        if (this.requests.get(gallery) === request) gallery.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('ron-merchandising', RonMerchandising);
})();
